import { Card, Display, Text, useRebrand } from "@growtherapy/sprout-ui";
import PreSessionImage from "../../../assets/images/pre-session-image.svg?react";
import { VisitorDrawerState } from "../../state";
import { ClientMeasuresCta } from "./ClientMeasuresCta";
import { VibesCtaWrapper } from "./VibesCta";
import { MoodSurveyCta } from "./MoodSurveyCta";
import { ReflectionTopicsCtaWrapper } from "./ReflectionTopicsCta";
import { useAtomValue } from "jotai";
import { WaitingRoomState } from "../types";
import { JoinSessionCta } from "./JoinSessionCta";
import { useAdmissionFlow } from "../../../hooks/useAdmissionFlow";
import { videoRoomTokenAtom } from "../../../video-sdk/core/room/state";
import { useTranslate } from "@growtherapy/i18n";

export interface PreSessionPromptProps {
  callToActionContent: React.ReactNode;
  state?: WaitingRoomState;
}

export type CTAProps = {
  openDrawer: (drawer: VisitorDrawerState) => void;
  state?: WaitingRoomState;
  variant?: PreSessionPromptVariant;
};

export enum PreSessionPromptVariant {
  CLIENT_MEASURES = "CLIENT_MEASURES",
  MOOD_SURVEY = "MOOD_SURVEY",
  REFLECTION_TOPICS = "REFLECTION_TOPICS",
  VIBES = "VIBES",
}

export const VariantComponents: Record<
  PreSessionPromptVariant,
  React.FC<CTAProps>
> = {
  [PreSessionPromptVariant.CLIENT_MEASURES]: ClientMeasuresCta,
  [PreSessionPromptVariant.MOOD_SURVEY]: MoodSurveyCta,
  [PreSessionPromptVariant.VIBES]: VibesCtaWrapper,
  [PreSessionPromptVariant.REFLECTION_TOPICS]: ReflectionTopicsCtaWrapper,
};

export function PreSessionPrompt({
  callToActionContent,
  state,
}: PreSessionPromptProps) {
  const t = useTranslate();
  const isRebrand = useRebrand();
  const { headingContent, bodyContent } =
    state === WaitingRoomState.ADMITTED
      ? {
          headingContent: t(
            "telehealth-core:waitingRoom.timeToJoinYourSession",
          ),
          bodyContent: t(
            "telehealth-core:waitingRoom.yourProviderIsReadyForYou",
          ),
        }
      : {
          headingContent: t("telehealth-core:waitingRoom.youreInTheRightPlace"),
          bodyContent: (
            <span>
              {t(
                "telehealth-core:waitingRoom.youCanJoinWhenYourProviderIsReady",
              )}
              <br />
              {t("telehealth-core:waitingRoom.theyHave10MinGracePeriod")}
            </span>
          ),
        };

  return (
    <Card className="flex flex-col mobile:border-x rounded-xl items-center max-w-[38rem] gap-y-10 sm:p-10">
      {!isRebrand && (
        <div className="hidden sm:block">
          <PreSessionImage
            aria-label={t(
              "telehealth-core:waitingRoom.aLightPurpleSketchOfAPairOfHandsClaspedTogether",
            )}
          />
        </div>
      )}

      <div className="flex flex-col gap-y-4 items-center">
        {isRebrand ? (
          <Display.Small>{headingContent}</Display.Small>
        ) : (
          <Text as="h2" variant="lg" className="font-medium">
            {headingContent}
          </Text>
        )}

        <Text className="text-neutral-700 text-center">{bodyContent}</Text>

        {callToActionContent}
      </div>
    </Card>
  );
}

export function PreSessionPromptWrapper({
  variant,
  openDrawer,
}: {
  variant: PreSessionPromptVariant;
  openDrawer: (drawer: VisitorDrawerState) => void;
}) {
  const admissionFlow = useAdmissionFlow();
  const hasRoomToken = !!useAtomValue(videoRoomTokenAtom);
  const isAdmitted = admissionFlow === "manual" && hasRoomToken;
  const CallToAction = isAdmitted ? JoinSessionCta : VariantComponents[variant];
  const state = isAdmitted
    ? WaitingRoomState.ADMITTED
    : WaitingRoomState.AWAITING_ADMISSION;

  return (
    <PreSessionPrompt
      state={state}
      callToActionContent={
        <CallToAction openDrawer={openDrawer} state={state} variant={variant} />
      }
    />
  );
}
